/**
 * TimeVsReturn Component
 *
 * Visualizes whether time spent on a piece was worth the financial return.
 * Normalizes hours and dollars into a universal metric: Calendar Days (30-day month).
 */

import { useMemo } from 'react';

interface TimeVsReturnProps {
  totalMonthlyHours: number;
  monthlyOverhead: number; // cents
  hoursSpent: number;
  price: number; // cents
}

export function TimeVsReturn({
  totalMonthlyHours,
  monthlyOverhead,
  hoursSpent,
  price,
}: TimeVsReturnProps) {
  const calculations = useMemo(() => {
    // Cost in Days: How many days of the month did I lose making this?
    const costInDays = totalMonthlyHours > 0
      ? (hoursSpent / totalMonthlyHours) * 30
      : 0;

    // Return in Days: How many days of overhead did this buy back?
    const returnInDays = monthlyOverhead > 0
      ? (price / monthlyOverhead) * 30
      : 0;

    // Percentages for bar widths (capped at 100 for display)
    const costPercent = Math.min((costInDays / 30) * 100, 100);
    const returnPercent = Math.min((returnInDays / 30) * 100, 100);

    // Is this a win?
    const isWin = returnInDays >= costInDays;

    // Overflow multiplier (for badge display)
    const overflowMultiplier = returnInDays > 30 ? returnInDays / 30 : null;

    // Freedom gap (net days)
    const freedomGap = returnInDays - costInDays;

    return {
      costInDays,
      returnInDays,
      costPercent,
      returnPercent,
      isWin,
      overflowMultiplier,
      freedomGap,
    };
  }, [totalMonthlyHours, monthlyOverhead, hoursSpent, price]);

  const {
    costInDays,
    returnInDays,
    costPercent,
    returnPercent,
    isWin,
    overflowMultiplier,
    freedomGap,
  } = calculations;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] font-medium uppercase tracking-wide text-charcoal-400">
          Time vs Return
        </h3>
        {overflowMultiplier && (
          <span className="text-[10px] font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
            {overflowMultiplier.toFixed(1)}x Month Coverage
          </span>
        )}
      </div>

      {/* Bar Container */}
      <div className="relative">
        {/* Track Background (Darker grey with inset depth) */}
        <div className="relative h-10 bg-charcoal-200 rounded-lg overflow-hidden shadow-inner">
          {/* Inset depth gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal-300/50 via-transparent to-white/10 pointer-events-none" />

          {/* Week Markers */}
          <div className="absolute inset-0 flex">
            {[25, 50, 75].map((percent) => (
              <div
                key={percent}
                className="absolute top-0 bottom-0 w-px bg-charcoal-300/60"
                style={{ left: `${percent}%` }}
              />
            ))}
          </div>

          {/* Clock Icon - Left side */}
          <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
            <svg className="w-4 h-4 text-charcoal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Cost Bar (Red/Salmon) - Time Sunk */}
          <div
            className="absolute top-0 bottom-0 left-0 bg-red-400/80 transition-all duration-500 ease-out"
            style={{ width: `${costPercent}%` }}
          >
            {/* Inner depth/dimension */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
            
            {/* Dotted line at end of time bar */}
            <div 
              className="absolute top-1 bottom-1 right-0 w-px border-r border-dashed border-charcoal-500/40"
            />
          </div>

          {/* Return Bar (White + Green surplus) - Money Earned */}
          {price > 0 && (
            <div
              className="absolute top-1.5 bottom-1.5 left-0 rounded transition-all duration-700 ease-out"
              style={{ width: `${returnPercent}%` }}
            >
              {/* White fill for portion up to cost */}
              <div 
                className="absolute top-0 bottom-0 left-0 bg-cream-100 rounded-l shadow-sm"
                style={{ 
                  width: returnPercent > 0 
                    ? `${Math.min((costPercent / returnPercent) * 100, 100)}%` 
                    : '0%',
                  borderTopRightRadius: returnPercent <= costPercent ? '0.25rem' : '0',
                  borderBottomRightRadius: returnPercent <= costPercent ? '0.25rem' : '0',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white to-charcoal-50 rounded-l" />
              </div>

              {/* Green fill for surplus portion (beyond cost) - more vibrant */}
              {returnPercent > costPercent && (
                <div 
                  className="absolute top-0 bottom-0 right-0 bg-emerald-200 rounded-r"
                  style={{ 
                    width: `${((returnPercent - costPercent) / returnPercent) * 100}%`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-emerald-100 to-emerald-300/80 rounded-r" />
                </div>
              )}

              {/* Dollar sign indicator at end */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-5 h-5 bg-teal-500 rounded-full shadow-md border-2 border-white flex items-center justify-center z-10">
                <span className="text-[10px] font-bold text-white">$</span>
              </div>
            </div>
          )}
        </div>

        {/* Week Labels */}
        <div className="flex justify-between mt-1.5 px-0.5">
          <span className="text-[9px] text-charcoal-300">Week 1</span>
          <span className="text-[9px] text-charcoal-300">Week 2</span>
          <span className="text-[9px] text-charcoal-300">Week 3</span>
          <span className="text-[9px] text-charcoal-300">30 Days</span>
        </div>
      </div>

      {/* Verdict - Bottom right only, no legend */}
      <div className="flex justify-end mt-2">
        <div className={`text-sm font-semibold ${isWin ? 'text-teal-600' : 'text-red-500'}`}>
          {isWin
            ? freedomGap > 0.1
              ? `+${freedomGap.toFixed(1)} days surplus`
              : 'Break even'
            : `${freedomGap.toFixed(1)} days deficit`
          }
        </div>
      </div>
    </div>
  );
}

export type { TimeVsReturnProps };
export default TimeVsReturn;
